import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';

export const reducedMotion = () => typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

export const cx = (...names) => names.filter(Boolean).join(' ');

// Returns the copy for the active language, falling back to English.
export function useCopy(copy) {
  const { language } = useLanguage();
  return { t: copy[language] || copy.en, zh: language === 'zh', language };
}

// Pages fade out briefly before a route change and fade back in on arrival.
export function FadeLink({ to, onClick, target, ...rest }) {
  const navigate = useNavigate();
  const location = useLocation();
  const handleClick = (event) => {
    onClick?.(event);
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || target) return;
    const next = new URL(to, window.location.origin + location.pathname);
    if (next.pathname === location.pathname || reducedMotion()) return;
    event.preventDefault();
    document.documentElement.classList.add('leaving');
    window.setTimeout(() => navigate(to), 200);
  };
  return <Link to={to} target={target} onClick={handleClick} {...rest} />;
}

// Plain anchors for the separately deployed apps (/learn/, /one/) and external sites.
export function ExternalLink({ href, children, newTab, ...rest }) {
  const external = /^https?:/.test(href);
  return <a href={href} {...(external || newTab ? { target: '_blank', rel: 'noopener noreferrer' } : {})} {...rest}>{children}</a>;
}

// Renders trusted, build-time SVG markup generated from the customer's Figma export.
export function Markup({ as: Tag = 'span', markup, ...rest }) {
  return <Tag {...rest} dangerouslySetInnerHTML={{ __html: markup }} />;
}

/*
 * A section that reveals its [data-reveal] children once, the first time it scrolls
 * into view. After an element's entrance transition, its data-reveal attribute is
 * removed so hover transforms defined by the design take over again.
 */
export function Section({ as: Tag = 'section', className, threshold = 0.18, rootMargin = '0px 0px -6% 0px', onEnter, children, ...rest }) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  const enterRef = useRef(onEnter);
  enterRef.current = onEnter;
  useEffect(() => {
    const node = ref.current;
    if (!node) return undefined;
    if (typeof IntersectionObserver === 'undefined') { setInView(true); enterRef.current?.(); return undefined; }
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        observer.disconnect();
        setInView(true);
        enterRef.current?.();
      }
    }, { threshold, rootMargin });
    observer.observe(node);
    return () => observer.disconnect();
  }, [threshold, rootMargin]);
  const handleTransitionEnd = (event) => {
    const element = event.target;
    if (event.propertyName === 'transform' && element.hasAttribute?.('data-reveal') && inView) element.removeAttribute('data-reveal');
  };
  return <Tag ref={ref} data-section="" className={cx(className, inView && 'in')} onTransitionEnd={handleTransitionEnd} {...rest}>{children}</Tag>;
}

// Light parallax on decorative layers only: [data-px] is the maximum shift as a fraction of the host height.
export function useParallax(deps = []) {
  useEffect(() => {
    if (reducedMotion()) return undefined;
    const elements = [...document.querySelectorAll('[data-px]')];
    if (!elements.length) return undefined;
    let ticking = false;
    const run = () => {
      ticking = false;
      const viewport = window.innerHeight;
      for (const element of elements) {
        const host = element.parentElement.getBoundingClientRect();
        if (host.bottom < 0 || host.top > viewport) continue;
        const progress = Math.max(-1, Math.min(1, (host.top + host.height / 2 - viewport / 2) / (viewport / 2 + host.height / 2)));
        element.style.transform = `translate3d(0,${(progress * parseFloat(element.dataset.px) * host.height).toFixed(1)}px,0)`;
      }
    };
    const onScroll = () => { if (!ticking) { ticking = true; requestAnimationFrame(run); } };
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', run);
    run();
    return () => { window.removeEventListener('scroll', onScroll); window.removeEventListener('resize', run); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}

// Native <dialog> lightbox used by the product detail screenshots.
export function useLightbox() {
  const dialogRef = useRef(null);
  const [image, setImage] = useState(null);
  const open = useCallback((src, alt) => {
    setImage({ src, alt });
    const dialog = dialogRef.current;
    if (dialog && !dialog.open) {
      dialog.showModal();
      requestAnimationFrame(() => dialog.classList.add('on'));
    }
  }, []);
  const close = useCallback(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    dialog.classList.remove('on');
    window.setTimeout(() => dialog.open && dialog.close(), reducedMotion() ? 0 : 280);
  }, []);
  const element = (
    <dialog ref={dialogRef} className="lb" aria-label={image?.alt || 'Enlarged screen'}
      onClick={(event) => { if (event.target === dialogRef.current) close(); }}
      onCancel={(event) => { event.preventDefault(); close(); }}>
      <button className="x" type="button" aria-label="Close" onClick={close}>✕</button>
      {image && <img src={image.src} alt={image.alt} />}
    </dialog>
  );
  return { open, element };
}
